/**
 * WalletManager — Phantom wallet connection singleton.
 *
 * Follows the MusicManager pattern: module-level state, localStorage persistence.
 * Supports Phantom browser extension and mobile deep-linking.
 *
 * Usage:
 *   WalletManager.connect()      // opens Phantom popup
 *   WalletManager.disconnect()   // clears session
 *   WalletManager.getPublicKey() // returns base58 string or null
 *   WalletManager.isConnected()  // boolean
 *   WalletManager.linkToAccount(handle) // prove ownership + link on server
 */

import XAuth from './XAuth';
import { walletLinkMessage } from '../shared/walletLinkMessage';

const STORAGE_KEY = 'woodchuck_wallet';

function getProvider() {
  if (typeof window !== 'undefined' && window.phantom?.solana?.isPhantom) {
    return window.phantom.solana;
  }
  return null;
}

function getStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function setStored(publicKey) {
  const prev = getStored();
  localStorage.setItem(STORAGE_KEY, JSON.stringify({
    publicKey,
    connectedAt: Date.now(),
    // Keep the server link only while it's still the same wallet
    linkedHandle: prev && prev.publicKey === publicKey ? prev.linkedHandle : null,
  }));
}

function setLinkedHandle(handle) {
  const stored = getStored();
  if (stored) localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...stored, linkedHandle: handle }));
}

const WalletManager = {
  /**
   * Check if Phantom wallet is installed.
   */
  isPhantomInstalled() {
    return !!getProvider();
  },

  /**
   * Get the Phantom deep link URL for mobile users without extension.
   */
  getPhantomInstallUrl() {
    return 'https://phantom.app/';
  },

  /**
   * Connect to Phantom wallet. Opens popup for user approval.
   * Returns the public key string on success.
   */
  async connect() {
    const provider = getProvider();
    if (!provider) {
      throw new Error('Phantom wallet not installed');
    }

    const resp = await provider.connect();
    const pubkey = resp.publicKey.toString();
    setStored(pubkey);
    return pubkey;
  },

  /**
   * Link the connected wallet to the signed-in player.
   * The wallet signs walletLinkMessage() so the server can verify ownership.
   *
   * @param {string} handle - Signed-in player's handle
   * @returns {Promise<{ ok: true } | { ok: false, error: string }>}
   */
  async linkToAccount(handle) {
    const provider = getProvider();
    const wallet = this.getPublicKey();
    const token = XAuth.getSessionToken();
    if (!provider || !wallet) return { ok: false, error: 'Wallet not connected' };
    if (!token || !handle) return { ok: false, error: 'Sign in with X first' };

    const issuedAt = new Date().toISOString();
    const message = new TextEncoder().encode(
      walletLinkMessage({ handle, wallet, issuedAt }),
    );

    let signature;
    try {
      const signed = await provider.signMessage(message, 'utf8');
      signature = btoa(String.fromCharCode(...signed.signature));
    } catch {
      return { ok: false, error: 'Signature request was rejected' };
    }

    try {
      const resp = await fetch('/api/payment/wallet', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ wallet, issuedAt, signature }),
      });
      if (resp.ok) {
        setLinkedHandle(handle);
        return { ok: true };
      }
      const data = await resp.json().catch(() => ({}));
      return { ok: false, error: data.error || 'Could not link wallet' };
    } catch {
      return { ok: false, error: 'Network error' };
    }
  },

  /** Whether this wallet was proven and linked to `handle` on the server. */
  isLinkedTo(handle) {
    const stored = getStored();
    return !!(stored && handle && stored.linkedHandle === handle);
  },

  /**
   * Silently reconnect if user previously approved this site.
   * No popup — uses Phantom's "trusted app" auto-connect.
   */
  async reconnect() {
    const stored = getStored();
    if (!stored) return null;

    const provider = getProvider();
    if (!provider) return null;

    try {
      const resp = await provider.connect({ onlyIfTrusted: true });
      const pubkey = resp.publicKey.toString();
      setStored(pubkey);
      return pubkey;
    } catch {
      // User revoked trust or Phantom isn't ready — clear stored
      return null;
    }
  },

  /**
   * Disconnect wallet and clear stored state.
   */
  disconnect() {
    const provider = getProvider();
    if (provider) {
      try { provider.disconnect(); } catch { /* ignore */ }
    }
    localStorage.removeItem(STORAGE_KEY);
  },

  /**
   * Get the connected public key (synchronous, from localStorage).
   */
  getPublicKey() {
    const stored = getStored();
    return stored ? stored.publicKey : null;
  },

  /**
   * Check if wallet is connected.
   */
  isConnected() {
    return !!this.getPublicKey();
  },

  /**
   * Get the Phantom provider for signing transactions.
   */
  getProvider() {
    return getProvider();
  },

  /**
   * Get a truncated display version of the public key.
   */
  getShortKey() {
    const key = this.getPublicKey();
    if (!key) return null;
    return key.slice(0, 4) + '...' + key.slice(-4);
  },
};

export default WalletManager;
